import React, { useContext, useEffect, useMemo, useState } from "react"
import { TreeSelect } from "primereact/treeselect"
import { Dropdown } from "primereact/dropdown"
import { useNavigate } from "react-router-dom"
import { UserContext } from "../../../../context/userContext"

import styles from "../../../../styles/SelectEnterpriseOrganization.module.css"

export default function SelectEnterpriseOrganization({
  enterpriseList,
  enterpriseOrganizations,
  fetching,
}) {
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()
  const [nodes, setNodes] = useState(null)
  const [selectedNodeKey, setSelectedNodeKey] = useState(null)
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(null)
  const [loadingNodes, setLoadingNodes] = useState(false)
  const [loadingSelectedNode, setLoadingSelectedNode] = useState(false)
  const [navigating, setNavigating] = useState(false)

  const organizationOptions = useMemo(() => {
    return enterpriseList.flatMap((enterprise) =>
      (enterpriseOrganizations[enterprise.enterprise_id] || [])
        .slice()
        .sort((a, b) =>
          (a.organization_name || "").localeCompare(b.organization_name || ""),
        )
        .map((organization) => ({
          label: organization.organization_name,
          value: organization.organization_id,
          enterpriseId: enterprise.enterprise_id,
        })),
    )
  }, [enterpriseList, enterpriseOrganizations])

  const selectorVariant = useMemo(() => {
    if (organizationOptions.length <= 1) {
      return "singleOrg"
    }

    if (enterpriseList.length === 1) {
      return "singleEnterprise"
    }

    return "multiEnterprise"
  }, [enterpriseList.length, organizationOptions.length])

  const hasSessionSelection =
    !!userCtx.session.user.enterprise_id && !!userCtx.session.user.organization_id

  const selectedNodeResolved = hasSessionSelection
    ? nodes?.some((enterpriseNode) =>
        enterpriseNode?.children?.some(
          (organizationNode) => organizationNode.key === selectedNodeKey,
        ),
      )
    : true

  const selectionLoading = fetching || loadingNodes || loadingSelectedNode
  const showSelectionLoading = selectionLoading || !selectedNodeResolved

  useEffect(() => {
    setLoadingNodes(true)

    // Build a tree structure for the TreeSelect component based on the enterprise and organization data.
    const enterpriseNodes = enterpriseList.map((enterprise) => {
      const enterpriseId = enterprise.enterprise_id
      const enterpriseName = enterprise.enterprise_name

      // Get the organizations for this enterprise and map them to the required format for TreeSelect.
      const organizationNodes = (enterpriseOrganizations[enterpriseId] || [])
        .slice()
        .sort((a, b) =>
          (a.organization_name || "").localeCompare(b.organization_name || ""),
        )
        .map((organization) => ({
          key: `${enterpriseId}::${organization.organization_id}`,
          label: organization.organization_name,
        }))

      // Return the enterprise node with its child organization nodes.
      return {
        key: enterpriseId,
        label: enterpriseName,
        selectable: false,
        children: organizationNodes,
      }
    })

    setNodes(enterpriseNodes)
    setLoadingNodes(false)
  }, [enterpriseList, enterpriseOrganizations])

  // Update the selected node key when the user's enterprise or organization changes.
  useEffect(() => {
    setLoadingSelectedNode(true)
    const enterpriseId = userCtx.session.user.enterprise_id
    const organizationId = userCtx.session.user.organization_id

    if (!enterpriseId || !organizationId) {
      setSelectedNodeKey(null)
      setSelectedOrganizationId(null)
      setLoadingSelectedNode(false)
      return
    }

    setSelectedNodeKey(`${enterpriseId}::${organizationId}`)
    setSelectedOrganizationId(organizationId)
    setLoadingSelectedNode(false)
  }, [userCtx.session.user.enterprise_id, userCtx.session.user.organization_id])

  useEffect(() => {
    if (selectorVariant !== "singleEnterprise") {
      return
    }

    if (!organizationOptions.length) {
      return
    }

    const hasSelectedOption = organizationOptions.some(
      (option) => option.value === selectedOrganizationId,
    )

    if (!hasSelectedOption) {
      setSelectedOrganizationId(organizationOptions[0].value)
    }
  }, [selectorVariant, organizationOptions, selectedOrganizationId])

  const handleTreeSelectChange = async (e) => {
    const selectedValue = e.value
    setSelectedNodeKey(selectedValue)

    if (!selectedValue) {
      return
    }

    // Only organization nodes use enterprise::organization keys.
    if (!selectedValue.includes("::")) {
      return
    }

    // Split the selected value into enterprise and organization IDs and update the user's context accordingly.
    const [, organizationId] = selectedValue.split("::")

    if (
      organizationId &&
      organizationId !== userCtx.session.user.organization_id
    ) {
      // Disable the TreeSelect while navigating to prevent multiple rapid changes.
      setNavigating(true)

      // Change the organization in the user context and navigate to the home page.
      await userCtx.changeOrganization(organizationId)

      // Return to the home page when the organization is changed.
      navigate("/", { replace: true })

      // Re-enable the TreeSelect after navigation is complete.
      setNavigating(false)
    }
  }

  const handleOrganizationDropdownChange = async (e) => {
    const nextOrganizationId = e.value
    setSelectedOrganizationId(nextOrganizationId)

    if (!nextOrganizationId || nextOrganizationId === userCtx.session.user.organization_id) {
      return
    }

    setNavigating(true)
    await userCtx.changeOrganization(nextOrganizationId)
    navigate("/", { replace: true })
    setNavigating(false)
  }

  const singleOrgName = organizationOptions[0]?.label || ""

  return (
    <div className={styles.treeSelectScope}>
      <div className={styles.selectorRow}>
        {selectorVariant === "multiEnterprise" && (
          <TreeSelect
            disabled={selectionLoading || navigating}
            selectionMode="single"
            value={selectedNodeResolved ? selectedNodeKey : null}
            onChange={handleTreeSelectChange}
            options={nodes}
            placeholder={
              showSelectionLoading ? "Loading..." : "Select Organization"
            }
            filter
            filterMode="strict"
          />
        )}

        {selectorVariant === "singleEnterprise" && (
          <Dropdown
            disabled={selectionLoading || navigating}
            value={selectedOrganizationId}
            onChange={handleOrganizationDropdownChange}
            options={organizationOptions}
            optionLabel="label"
            optionValue="value"
            placeholder={
              showSelectionLoading ? "Loading..." : "Select Organization"
            }
            filter
            filterBy="label"
          />
        )}

        {selectorVariant === "singleOrg" && (
          <div className={styles.selectorStatic} title={singleOrgName}>
            <span className={styles.selectorStaticText}>
              {showSelectionLoading ? "Loading..." : singleOrgName}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}